import React, {
  useState,
  useContext,
  useEffect,
  Fragment,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  createRiskManagementPlan,
  updateRiskManagementPlan,
  createRevisionHistory,
} from "../../helper";
import DialogBoxComponent from "../../../../components/DialogBoxComponent";
import { CardActions, Stack, Typography } from "@mui/material";
import { Container } from "@mui/material";
import { Card } from "@mui/material";
import { CardContent, CardHeader } from "@mui/material";
import RichTextBoxTabPanel from "../../../../components/RichTextBoxEditerTabs";
import { useRiskManagementContext } from "../../../../context/RiskManagement/RiskManagementContext";
import { RiskManagementPlan } from "../../modal";
import { RevisionHistory } from "../RevisionHistory";
import { convertToUTC, isClientAdmin } from "../../../../utils/ConvertDate";
import { useUserContext } from "../../../../context/auth/UserContext";
import AutoVersionIncrement from "../../../../components/AutoVersionIncrement";
import { useLocation } from "react-router-dom";
import { useAccessRights } from "../../../../utils/useAccessRights";

const initialFormState: RiskManagementPlan = {
  overview: "",
  purpose: "",
  scope: "",
  definition: "",
  plan: "",
  compliance: "",
  relatedPolicies: "",
  version: "",
  originalVersion: "",
  versionIncrement: "",
  dateApproved: null,
  status: "",
};

const getFieldControlStyles = () => ({
  backgroundColor: "#F1F5F7",
  ".MuiOutlinedInput-root": {
    border: "none",
    "& fieldset": {
      border: "none",
    },
  },
  borderRadius: "5px",
});

export const RiskManagementForm = () => {
  const navigate = useNavigate();
  const riskManagementPageAccess = useAccessRights("Risk Management - CS & SC");
  const location = useLocation();
  const { userId, companyProfileID, userAccess } = useUserContext();
  const [planHeaderTitle, setPlanHeaderTitle] = useState<string>(
    "Create New Cybersecurity Risk Manageent Plan"
  );
  const [openRevisionHistory, setOpenRevisionHistory] = useState(false);
  const [formValues, setFormValues] = useState(initialFormState);
  const [selectedTab, setSelectedTab] = useState<string>("overview");
  const [isUpdated, setIsUpdated] = useState(false);
  const isUpdatedRef = useRef(isUpdated);
  const [isUserStillOnPage, setIsUserStillOnPage] = useState(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
    openEditConfirmationDialog: false,
  });

  const { tabValue, planDetails, setPlanDetails, setSelectedRiskManagement } =
    useRiskManagementContext();
  const tabDetalis = [
    {
      label: "overview",
      key: "overview",
      id: "tab-overview",
      editerValue: formValues.overview,
    },
    {
      label: "purpose",
      key: "purpose",
      id: "tab-purpose",
      editerValue: formValues.purpose,
    },
    {
      label: "scope",
      key: "scope",
      id: "tab-scope",
      editerValue: formValues.scope,
    },
    {
      label: "definition",
      key: "definition",
      id: "tab-definition",
      editerValue: formValues.definition,
    },
    {
      label: "plan",
      key: "plan",
      id: "tab-plan",
      editerValue: formValues.plan,
    },
    {
      label: "compliance",
      key: "compliance",
      id: "tab-compliance",
      editerValue: formValues.compliance,
    },
    {
      label: "Related Documents",
      key: "relatedPolicies",
      id: "tab-relatedPolicies",
      editerValue: formValues.relatedPolicies,
    },
  ];

  // Handlers
  const resetForm = () => {
    setFormValues(initialFormState);
    setPlanDetails({});
    setSelectedRiskManagement(null);
  };

  const handleCancel = async () => {
    resetForm();
    navigate("/risk-management");
  };

  const handleCloseDialog = () => {
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
    resetForm();
    navigate("/risk-management");
  };

  const handleCloseEditDialog = () => {
    setFormValues((prevVal) => ({ ...prevVal, status: "Approved" }));
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
  };

  const handleTabEditerChange = (val: any, key: any) => {
    handleFormChange({
      target: { value: val, id: key },
    });
  };

  const handleFormChange = (e: { target: { id: any; value: any } }) => {
    const { id, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [id]: value,
    }));
  };

  const isPlanSelected = () => {
    return (
      planDetails?.csRiskManagementPlanID || planDetails?.supplyChainPlanID
    );
  };

  const handleCreatePlan = async () => {
    const date = new Date();
    // const approvalDate =
    //   formValues?.dateApproved && formValues.dateApproved.toDate();
    const payload = {
      overview: formValues.overview,
      purpose: formValues.purpose,
      scope: formValues.scope,
      definition: formValues.definition,
      plan: formValues.plan,
      compliance: formValues.compliance,
      relatedPolicies: formValues.relatedPolicies,
      version: formValues.version,
      dateApproved: convertToUTC(formValues.dateApproved),
      status: formValues.status,
      createDate: date,
      createdBy: userId,
      companyProfileID: companyProfileID,
      approvedBy: formValues.status === "Approved" ? userId : 0,
      isEditedByUserId: "0",
    };
    // console.log("handleCreatePlan payload",payload)
    const res = await createRiskManagementPlan(
      tabValue === 0 ? "cyberSecurity" : "supplyChain",
      payload
    );
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Plan successfully created",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleRevisionHistoryCancel = () => {
    setOpenRevisionHistory(false);
  };

  const handleRevisionHistoryCreate = async (val: any) => {
    const date = new Date();
    // const approvalDate = formValues?.dateApproved
    //   ? dayjs.isDayjs(formValues.dateApproved)
    //     ? formValues.dateApproved
    //     : dayjs(formValues.dateApproved).isValid()
    //       ? dayjs(formValues.dateApproved).toDate()
    //       : null
    //   : null;
    const payload = {
      descriptionOfChanges: val,
      comments: val,
      status: formValues.status,
      dateApproved: convertToUTC(formValues.dateApproved),
      version: formValues.version,
      createDate: date,
      createdBy: userId,
      approvedBy: userId,
      companyProfileID: companyProfileID,
      ...(tabValue === 0
        ? { csRiskManagementPlanID: planDetails.csRiskManagementPlanID }
        : { supplyChainPlanID: planDetails.supplyChainPlanID }),
    };
    const res = await createRevisionHistory(tabValue === 0, payload);
    if (res.succeeded === true) {
      handleEditPlan();
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleEditRevision = () => {
    if (
      formValues.status === "Approved" &&
      formValues.status !== planDetails.status
    ) {
      setOpenRevisionHistory(true);
    } else if (
      planDetails.status === "Approved" &&
      planDetails.status === formValues.status
    ) {
      setDialogState({
        title: "Confirmation",
        body: "Editing an Approved record will change the status to Ready to Approve . Do you want to continue?",
        action: "OK",
        actionCancel: "Cancel",
        openSuccessDialogBox: false,
        openEditConfirmationDialog: true,
      });
      setFormValues((prevVal) => ({ ...prevVal, status: "Ready to Approve" }));
    } else {
      handleEditPlan();
    }
  };

  const handleEditPlan = async () => {
    setIsUpdated(true);
    const date = new Date();
    // const approvalDate = formValues?.dateApproved
    //   ? dayjs.isDayjs(formValues.dateApproved)
    //     ? formValues.dateApproved
    //     : dayjs(formValues.dateApproved).isValid()
    //       ? dayjs(formValues.dateApproved).toDate()
    //       : null
    //   : null;
    const payload = {
      overview: formValues.overview,
      purpose: formValues.purpose,
      scope: formValues.scope,
      definition: formValues.definition,
      plan: formValues.plan,
      compliance: formValues.compliance,
      relatedPolicies: formValues.relatedPolicies,
      version: formValues.version,
      dateApproved: convertToUTC(formValues.dateApproved),
      status: formValues.status,
      updateDate: date,
      updateBy: userId,
      approvedBy: userId,
      ...(planDetails?.supplyChainPlanID
        ? { supplyChainPlanID: planDetails.supplyChainPlanID }
        : { csRiskManagementPlanID: planDetails.csRiskManagementPlanID }),
      isEditedByUser: false,
      isEditedByUserId: "0",
    };
    // console.log("handleEditPlan payload",payload)
    const res = await updateRiskManagementPlan(
      tabValue === 0 ? "cyberSecurity" : "supplyChain",
      payload
    );
    if (res.succeeded === true) {
      handleRevisionHistoryCancel();
      setDialogState({
        title: "Success",
        body: "Plan successfully updated",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const disableVersionChange = () => {
    if (
      isPlanSelected() &&
      planDetails.status !== formValues.status &&
      formValues.status === "Approved"
    ) {
      return false;
    }
    return true;
  };

  const disableForm = () => {
    const {
      version,
      status,
      dateApproved,
      overview,
      purpose,
      scope,
      definition,
      plan,
      compliance,
      relatedPolicies,
      versionIncrement,
    } = formValues;

    if (
      version === "" ||
      status === "" ||
      overview === "" ||
      purpose === "" ||
      scope === "" ||
      definition === "" ||
      plan === "" ||
      compliance === "" ||
      relatedPolicies === ""
    ) {
      return true;
    }

    if (status === "Approved") {
      if (
        !dateApproved ||
        (!disableVersionChange() && versionIncrement === "")
      ) {
        return true;
      }
    }
  };

  useEffect(() => {
    if (formValues.status !== "Approved") {
      setFormValues((prevVal) => ({
        ...prevVal,
        versionIncrement: "",
        version: formValues.originalVersion,
        dateApproved: null,
      }));
    }
  }, [formValues.status]);

  useEffect(() => {
    if (isPlanSelected()) {
      setFormValues({
        overview: planDetails.overview,
        purpose: planDetails.purpose,
        scope: planDetails.scope,
        definition: planDetails.definition,
        plan: planDetails.plan,
        compliance: planDetails.compliance,
        relatedPolicies: planDetails.relatedPolicies,
        version: planDetails.version,
        originalVersion: planDetails.version,
        versionIncrement: "",
        dateApproved: planDetails.dateApproved,
        status: planDetails.status,
      });
      if (tabValue == 0) {
        setPlanHeaderTitle("Edit Cybersecurity Risk Management Plan");
      } else {
        setPlanHeaderTitle("Edit Supply Chain Management Plan");
      }
    } else {
      setFormValues((prevValues: any) => ({
        ...prevValues,
        status: "Not Approved",
        version: "1.0",
        originalVersion: "1.0",
      }));
      if (tabValue == 0) {
        setPlanHeaderTitle("Create Cybersecurity Risk Management Plan");
      } else {
        setPlanHeaderTitle("Create Supply Chain Management Plan");
      }
    }
  }, [planDetails]);

  useEffect(() => {
    console.log("isUpdated", isUpdated);
    isUpdatedRef.current = isUpdated;
  }, [isUpdated]);

  const handleExit = async (isEdited: boolean) => {
    console.log("handleExit", isUpdatedRef.current);
    if (isPlanSelected() && !isUpdatedRef.current) {
      const date = new Date();
      const payload = {
        ...planDetails,
        isEditedByUser: isEdited,
        isEditedByUserId: userId + "",
        updateBy: userId,
        updateDate: date,
      };
      try {
        const resUpdate = await updateRiskManagementPlan(
          tabValue === 0 ? "cyberSecurity" : "supplyChain",
          payload
        );
        console.log("handleExit resUpdate", resUpdate);
        if (resUpdate) {
          setIsUserStillOnPage(false);
          return resUpdate;
        }
      } catch (error) {
        console.log("@@Error", error);
      }
    }
  };

  useEffect(() => {
    return () => {
      handleExit(false);
    };
  }, [location]);

  useEffect(() => {
    console.log("isUserStillOnPage >> ", isUserStillOnPage);
    if (isUserStillOnPage) {
      handleExit(true);
    }
  }, [isUserStillOnPage]);

  useEffect(() => {
    console.log("beforeunload");

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      console.log("handleBeforeUnload", event);
      event.preventDefault();
      await handleExit(false);
      setIsUserStillOnPage(true);
      const message =
        "You have unsaved changes. Are you sure you want to leave? ";
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Fragment>
      <Container sx={{ p: 1, height: "100%", width: "100%" }}>
        <Card
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <CardContent>
            <Typography
              style={{
                opacity: 0.7,
                fontWeight: "400",
              }}
            >
              {planHeaderTitle}
            </Typography>
            <Container sx={{ p: 2 }}>
              <Stack direction="column" spacing={3}>
                <Grid container justifyContent="center">
                  <RichTextBoxTabPanel
                    readonly={false}
                    tabs={tabDetalis}
                    handleTabEditerChange={handleTabEditerChange}
                  />
                </Grid>
                <Grid container columnSpacing={3}>
                  <AutoVersionIncrement
                    disableIncrement={disableVersionChange()}
                    version={formValues.version}
                    originalVersion={formValues.originalVersion}
                    versionIncrement={formValues.versionIncrement}
                    onChange={(version, versionIncrement) =>
                      setFormValues({
                        ...formValues,
                        version,
                        versionIncrement,
                      })
                    }
                  />
                  <Grid xs={12} sm={6}>
                    <Typography
                      style={{
                        fontWeight: "400",
                        fontStyle: "italic",
                        margin: "0.5rem 0",
                        opacity: 0.7,
                      }}
                    >
                      Status
                    </Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={getFieldControlStyles}
                    >
                      <Select
                        id="status"
                        value={formValues.status}
                        onChange={(e) =>
                          handleFormChange({
                            target: { value: e.target.value, id: "status" },
                          })
                        }
                      >
                        <MenuItem value="Not Approved">Not Approved</MenuItem>
                        <MenuItem value="Ready to Approve">
                          Ready to Approve
                        </MenuItem>
                        <MenuItem
                          value="Approved"
                          disabled={!riskManagementPageAccess?.isApproved}
                        >
                          Approved
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <Typography
                      style={{
                        opacity: 0.7,
                        fontWeight: "400",
                        fontStyle: "italic",
                        margin: "0.5rem 0",
                      }}
                    >
                      Approval Date
                    </Typography>
                    <DatePicker
                      sx={{
                        ".MuiOutlinedInput-root": {
                          border: "none", // Remove the border
                          boxShadow: "none", // Remove any box shadow that might appear as a border
                        },
                        ".MuiInputBase-root": {
                          minHeight: "30px", // Adjust the height as needed
                          fontSize: "0.875rem",
                          backgroundColor: "#F1F5F7",
                          padding: "4px 8px",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          border: "none", // Ensure the outline is also removed
                        },
                        ".MuiOutlinedInput-input": {
                          padding: "6px",
                        },
                      }}
                      value={
                        formValues?.dateApproved
                          ? dayjs(formValues.dateApproved)
                          : null
                      }
                      onChange={(val: Dayjs | null) => {
                        if (val) {
                          handleFormChange({
                            target: {
                              id: "dateApproved",
                              value: val,
                            },
                          });
                        } else {
                          handleFormChange({
                            target: {
                              id: "dateApproved",
                              value: null,
                            },
                          });
                        }
                      }}
                      slotProps={{ field: { clearable: true } }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Container>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#0066FF1A",
                color: "#0066FF",
                "&:hover": {
                  bgcolor: "#0066FF1A",
                  "& .MuiTypography-root": {
                    color: "white",
                  },
                },
                boxShadow: 0,
                textTransform: "none",
              }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            {isPlanSelected() ? (
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  boxShadow: 0,
                  backgroundColor: "#0066FF",
                }}
                disabled={disableForm() || !riskManagementPageAccess?.isUpdate}
                onClick={handleEditRevision}
              >
                Edit
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  boxShadow: 0,
                  backgroundColor: "#0066FF",
                }}
                onClick={handleCreatePlan}
                disabled={disableForm() || !riskManagementPageAccess?.isCreate}
              >
                Create
              </Button>
            )}
          </CardActions>
        </Card>
      </Container>
      {
        <RevisionHistory
          openDialogBox={openRevisionHistory}
          handleCancel={handleRevisionHistoryCancel}
          handleCreate={(val: any) => handleRevisionHistoryCreate(val)}
        />
      }
      {
        // Edit confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openEditConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleEditPlan}
          handleCancel={handleCloseEditDialog}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
